/**
 * Walk 3 — the tenant switcher, and the provider that keeps the choice in the address bar.
 *
 * <p>Only a principal with more than one allowed tenant (an operator or platform admin) ever sees
 * any of this. A client principal gets no switcher, no `?tenant=` written into their URL, and a
 * provider that resolves to exactly the tenant they resolved to before — `allowed_tenant_ids[0]`.
 * The pure rules live in `api/tenantSelection.ts`.</p>
 *
 * <h3>Where the choice lives</h3>
 * <ol>
 *   <li><b>The URL</b> (`?tenant=<slug>`) — the truth, so a link pasted to a colleague opens the
 *       same firm's page. In-page links (a file row, the chase list) are written without it, so the
 *       provider puts it back with a `replace` navigation whenever a multi-tenant viewer lands on a
 *       page whose URL does not name the tenant they are reading.</li>
 *   <li><b>localStorage</b> — a convenience, read only when the URL names nothing, and wrapped so a
 *       private window or blocked storage simply falls through to the default.</li>
 *   <li><b>The default</b> — `allowed_tenant_ids[0]`, unchanged.</li>
 * </ol>
 *
 * <h3>Nothing leaks across a switch</h3>
 * <p>Every read is keyed by tenant (query key and, in the fetcher's ETag memo, querystring), so a
 * cached answer is never served under another tenant. On a switch the provider still DROPS the
 * previous tenant's cached queries and clears the ETag memo: a switch is rare, and "the old firm's
 * data is gone from this tab" is a simpler property to trust than "it is there but unreachable".</p>
 */
import { useContext, useEffect, useMemo, useRef, useState, type ChangeEvent, type ReactNode } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import * as etagCache from "../api/etag-cache.js";
import { useClientTenant, useMe } from "../api/portalQueries.js";
import {
  TENANT_PARAM,
  TenantSelectionContext,
  canSwitchTenant,
  readRememberedTenant,
  rememberTenant,
  resolveTenantId,
  tenantSummary,
  withTenantParam,
  type TenantSelection,
} from "../api/tenantSelection.js";
import type { TenantSummary } from "../api/types.js";

/**
 * Where a switch lands: the new tenant's LANDING route, which sends them to their first nav entry.
 *
 * <p>Not "the same page under the new tenant": the tenants a switcher serves have different pages
 * (one has reports, another only an onboarding board), a file id belongs to the tenant being
 * left, and the board's filters are about that tenant's files. So a switch starts clean — the
 * query carries the tenant and nothing else.</p>
 */
export function switchTarget(tenantSlug: string): { pathname: string; search: string } {
  return { pathname: "/", search: withTenantParam("", tenantSlug) };
}

export function TenantSelectionProvider({ children }: { children: ReactNode }): JSX.Element {
  const me = useMe();
  const location = useLocation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  // The URL wins whenever it names a tenant (including the back button returning to one). The
  // sticky copy only answers for a URL that names none — an in-page link, or a fresh visit, where
  // it starts from the remembered choice.
  const urlToken = new URLSearchParams(location.search).get(TENANT_PARAM);
  const [sticky, setSticky] = useState<string | null>(() => urlToken ?? readRememberedTenant());
  const requested = urlToken ?? sticky;
  useEffect(() => {
    if (urlToken !== null) setSticky(urlToken);
  }, [urlToken]);

  const multi = canSwitchTenant(me.data);
  const tenantId = resolveTenantId(me.data, requested);
  const slug = tenantSummary(me.data, tenantId)?.slug ?? null;

  // Keep the address bar naming the tenant being read (multi-tenant viewers only). `/` is skipped:
  // the landing route redirects straight on, carrying the query with it.
  useEffect(() => {
    if (!multi || slug === null || location.pathname === "/") return;
    if (urlToken === slug) return;
    navigate(
      { pathname: location.pathname, search: withTenantParam(location.search, slug), hash: location.hash },
      { replace: true },
    );
  }, [multi, slug, urlToken, location.pathname, location.search, location.hash, navigate]);

  useEffect(() => {
    if (multi && slug !== null) rememberTenant(slug);
  }, [multi, slug]);

  // Drop the previous tenant's cached answers once the switch has rendered (by then every mounted
  // query has moved to its new key, so nothing observed is pulled out from under a page).
  const previous = useRef<string | null>(null);
  useEffect(() => {
    const old = previous.current;
    previous.current = tenantId;
    if (old === null || tenantId === null || old === tenantId) return;
    queryClient.removeQueries({ predicate: (query) => query.queryKey.includes(old) });
    etagCache.clear();
  }, [tenantId, queryClient]);

  // A switch navigates from an EFFECT, never from the change handler itself (#2358). A choice can
  // land between a render's commit and its effects — a busy main thread. The URL-keeping effect
  // above may then still be pending for the page being left, holding that page's address in its
  // closure. Had the handler navigated, that pending effect would run next and `replace` the
  // switch's history entry with the OLD tenant's address, and the viewer would stay on the old
  // firm for good. React flushes every pending effect before it renders the next update, so an
  // effect of the render that recorded the choice always runs after them: the switch lands last.
  const [pendingSwitch, setPendingSwitch] = useState<string | null>(null);
  useEffect(() => {
    if (pendingSwitch === null) return;
    setPendingSwitch(null);
    navigate(switchTarget(pendingSwitch));
  }, [pendingSwitch, navigate]);

  const value = useMemo<TenantSelection>(
    () => ({
      requested,
      // The URL is the ONE driver of a switch: navigating is the whole act, and the provider
      // re-resolves from the new address. (Setting the TENANT here as well would render the old
      // page under the new tenant for a frame — and let the URL-keeping effect above "correct" the
      // old address before the navigation landed.) The pending slug above is not a tenant: it
      // changes nothing that renders, and only schedules that navigation.
      choose: (tenant: TenantSummary) => setPendingSwitch(tenant.slug),
    }),
    [requested],
  );

  return <TenantSelectionContext.Provider value={value}>{children}</TenantSelectionContext.Provider>;
}

/**
 * The switcher: a compact native select under the brand, listing the tenants by display name.
 * Renders NOTHING for a principal with one tenant — a client never sees it, in any state.
 */
export function TenantSwitcher() {
  const { tenants, tenantId } = useClientTenant();
  const selection = useContext(TenantSelectionContext);
  if (selection === null || tenants.length < 2 || tenantId === null) return null;

  const change = (event: ChangeEvent<HTMLSelectElement>) => {
    const next = tenants.find((t) => t.id === event.target.value);
    if (next && next.id !== tenantId) selection.choose(next);
  };

  return (
    <label className="portal__tenant">
      <span className="portal__tenant-label">Viewing</span>
      <select className="portal__tenant-select" value={tenantId} onChange={change}>
        {tenants.map((tenant) => (
          <option key={tenant.id} value={tenant.id}>
            {tenant.display_name}
          </option>
        ))}
      </select>
    </label>
  );
}

/** The `?tenant=` query a nav link should carry: the slug for a multi-tenant viewer, else none. */
export function useTenantQuery(): string {
  const { tenants, tenantSlug } = useClientTenant();
  return tenants.length > 1 && tenantSlug !== null ? `?${new URLSearchParams({ [TENANT_PARAM]: tenantSlug })}` : "";
}
